/**
 * PlateListView — grouped, collapsible task list. Groups by status / priority /
 * entity / assignee (driven by the useTasks buckets). Empty groups are hidden;
 * done tasks are filtered unless "Show done" is on.
 */

import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, ChevronRight, CheckCircle2, Sparkles } from 'lucide-react';
import type { TodoItem, TaskStatus, TaskPriority, TaskEntityKind } from '@/types/schema';
import {
  STATUS_ORDER,
  PRIORITY_ORDER,
  getStatusTheme,
  getPriorityTheme,
  getEntityTheme,
} from '@/config/entityThemes';
import type { AssigneeGroup } from '@/hooks/useTasks';
import { PlateCard } from './PlateCard';
import { Avatar } from '@/components/common/Avatar';

export type GroupBy = 'status' | 'priority' | 'assignee' | 'entity';

interface Section {
  key: string;
  label: string;
  dotClass: string;
  items: TodoItem[];
  avatar?: { name: string; userId: string };
}

interface PlateListViewProps {
  groupBy: GroupBy;
  byStatus: Record<TaskStatus, TodoItem[]>;
  byPriority: Record<TaskPriority, TodoItem[]>;
  byEntity: Record<TaskEntityKind, TodoItem[]>;
  byAssignee: AssigneeGroup[];
  showDone: boolean;
  /** True when the "Tagged in" mentions section above has items — the empty
   *  state must NOT celebrate a "clean plate" while the user is tagged in
   *  things that still need attention. */
  hasTaggedItems?: boolean;
  onToggleComplete: (task: TodoItem, done: boolean) => void;
  onOpen?: (task: TodoItem) => void;
}

export function PlateListView({
  groupBy,
  byStatus,
  byPriority,
  byEntity,
  byAssignee,
  showDone,
  hasTaggedItems = false,
  onToggleComplete,
  onOpen,
}: PlateListViewProps) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const toggle = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const sections = buildSections(groupBy, { byStatus, byPriority, byEntity, byAssignee })
    .map((s) => ({
      ...s,
      items: showDone ? s.items : s.items.filter((t) => t.status !== 'done'),
    }))
    .filter((s) => s.items.length > 0);

  if (sections.length === 0) {
    // Tagged-in items still need attention — no "clean plate" celebration.
    // Copy stays filter-neutral: tasks may exist but be hidden by the search
    // query or the show-done toggle.
    if (hasTaggedItems) {
      return (
        <div className="text-center py-10">
          <p className="text-sm font-semibold text-theme-secondary">No tasks to show</p>
          <p className="text-xs text-theme-muted mt-1">
            You're still tagged in items above — clear those, add a task, or adjust your filters.
          </p>
        </div>
      );
    }
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: 'spring', stiffness: 280, damping: 24 }}
        className="flex flex-col items-center justify-center py-16 gap-3"
      >
        <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center relative">
          <CheckCircle2 className="w-8 h-8" strokeWidth={2.2} />
          <motion.div
            animate={{ y: [0, -3, 0], opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="absolute -top-1.5 -right-1.5"
          >
            <Sparkles className="w-4 h-4 text-emerald-400/70" />
          </motion.div>
        </div>
        <div className="text-center">
          <p className="text-sm font-bold text-theme-primary">Clean plate</p>
          <p className="text-xs text-theme-muted mt-1 max-w-[260px]">
            Nothing here right now. Add a task above, or switch filters to see more.
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="space-y-4">
      {sections.map((section) => {
        const isCollapsed = collapsed.has(section.key);
        return (
          <div key={section.key}>
            <button
              type="button"
              onClick={() => toggle(section.key)}
              className="w-full flex items-center gap-2 mb-2 group"
            >
              {isCollapsed ? (
                <ChevronRight className="w-4 h-4 text-theme-muted" />
              ) : (
                <ChevronDown className="w-4 h-4 text-theme-muted" />
              )}
              {section.avatar ? (
                <Avatar name={section.avatar.name} userId={section.avatar.userId} size="xs" />
              ) : (
                <span className={`w-2.5 h-2.5 rounded-full ${section.dotClass}`} />
              )}
              <span className="text-sm font-bold text-theme-primary">{section.label}</span>
              <span className="text-xs font-semibold text-theme-muted bg-surface-raised px-2 py-0.5 rounded-full">
                {section.items.length}
              </span>
            </button>

            <AnimatePresence initial={false}>
              {!isCollapsed && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="space-y-2">
                    <AnimatePresence initial={false}>
                      {section.items.map((task) => (
                        <PlateCard
                          key={task.id}
                          task={task}
                          onToggleComplete={onToggleComplete}
                          onOpen={onOpen}
                        />
                      ))}
                    </AnimatePresence>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}

function buildSections(
  groupBy: GroupBy,
  data: {
    byStatus: Record<TaskStatus, TodoItem[]>;
    byPriority: Record<TaskPriority, TodoItem[]>;
    byEntity: Record<TaskEntityKind, TodoItem[]>;
    byAssignee: AssigneeGroup[];
  },
): Section[] {
  if (groupBy === 'status') {
    return STATUS_ORDER.map((s) => {
      const theme = getStatusTheme(s);
      return { key: s, label: theme.label, dotClass: theme.dot, items: data.byStatus[s] ?? [] };
    });
  }
  if (groupBy === 'priority') {
    return PRIORITY_ORDER.map((p) => {
      const theme = getPriorityTheme(p);
      return { key: p, label: theme.label, dotClass: theme.dot, items: data.byPriority[p] ?? [] };
    });
  }
  if (groupBy === 'assignee') {
    return data.byAssignee.map((g) => ({
      key: g.userId,
      label: g.userName,
      dotClass: 'bg-slate-400',
      items: g.tasks,
      avatar: { name: g.userName, userId: g.userId },
    }));
  }
  // entity
  return (Object.keys(data.byEntity) as TaskEntityKind[])
    .map((k) => {
      const theme = getEntityTheme(k);
      return { key: k, label: theme.label, dotClass: theme.dot, items: data.byEntity[k] ?? [] };
    })
    .sort((a, b) => b.items.length - a.items.length);
}
